import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_URL } from "../../config"; // Adjust path if needed

function ManageBanners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  
  const [imageUrl, setImageUrl] = useState("");
  const [alertModal, setAlertModal] = useState({ show: false, message: "", type: "success" });
  
  // 🔥 NEW: Custom Modal for Delete Confirmation
  const [confirmModal, setConfirmModal] = useState(null); 

  const token = localStorage.getItem("token"); // Admin token

  // 1. FETCH BANNERS (🔥 useCallback fixed the ESLint Warning)
  const fetchBanners = useCallback(async () => {
    setLoading(true); // 🔥 Refresh dabane par Shimmer aayega
    try {
      const res = await axios.get(`${API_URL}/api/banners`); // Public route
      if (res.data.success) {
        setBanners(res.data.banners);
      }
    } catch (error) {
      console.error("Error fetching banners:", error);
      showModal("Failed to load banners. Check connection.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  // 2. ADD BANNER
  const handleAddBanner = async (e) => {
    e.preventDefault();
    if (!imageUrl.trim()) {
      return showModal("Please enter an image URL.", "error");
    }

    setUploading(true);
    try {
      // 🔥 FIX: Added '/admin' to the URL
      const res = await axios.post(
        `${API_URL}/api/admin/banner/add`,
        { imageUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        showModal("Banner added successfully!", "success");
        setImageUrl(""); // Clear input
        fetchBanners(); // Refresh list
      }
    } catch (error) {
      console.error("Add Banner Error:", error);
      showModal(error.response?.data?.message || "Failed to add banner", "error");
    } finally {
      setUploading(false);
    }
  };

  // 3. ACTUAL DELETE FUNCTION
  const executeDelete = async () => {
    if (!confirmModal) return;
    
    setDeletingId(confirmModal);
    try {
      // 🔥 FIX: Added '/admin' to the URL
      const res = await axios.delete(
        `${API_URL}/api/admin/banner/${confirmModal}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        showModal("Banner deleted successfully!", "success");
        fetchBanners(); // Refresh list
      }
    } catch (error) {
      console.error("Delete Banner Error:", error);
      showModal(error.response?.data?.message || "Failed to delete banner", "error");
    } finally {
      setDeletingId(null);
      setConfirmModal(null); // Close confirm modal
    }
  };

  // Helper for Modal
  const showModal = (message, type) => {
    setAlertModal({ show: true, message, type });
  };

  return (
    <div className="container-fluid p-4 animate-fade-in" style={{ background: "#f8f9fc", minHeight: "100vh" }}>
      
      {/* === HEADER & REFRESH === */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold text-dark mb-0"><i className="bi bi-images text-danger me-2"></i> Manage Banners</h3>
          <p className="text-muted small mb-0">Add or remove slider images for the Home Page.</p>
        </div>
        <button 
            className="btn btn-outline-dark rounded-pill px-4 shadow-sm d-flex align-items-center gap-2"
            onClick={() => fetchBanners()}
            disabled={loading}
        >
          {/* 🔥 FIX: Spin animation perfectly synced with loading */}
          <i className={`bi bi-arrow-clockwise ${loading ? "spin-animation" : ""}`}></i> 
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* === ADD BANNER FORM === */}
      <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
        <div className="card-header border-0 bg-white pt-4 pb-0 px-4">
            <h6 className="fw-bold text-dark">Add New Banner</h6>
        </div>
        <div className="card-body p-4">
          <form onSubmit={handleAddBanner} className="d-flex gap-3 align-items-center flex-wrap flex-md-nowrap">
            <div className="form-floating flex-grow-1">
              <input 
                type="url" 
                className="form-control bg-light border-0 shadow-none" 
                id="bannerUrlInput" 
                placeholder="https://example.com/image.jpg"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                required
              />
              <label htmlFor="bannerUrlInput" className="text-muted"><i className="bi bi-link-45deg"></i> Paste Image URL Here</label>
            </div>
            <button 
                type="submit" 
                className="btn py-3 px-4 rounded-3 shadow-sm text-white fw-bold d-flex justify-content-center align-items-center flex-shrink-0"
                style={{ background: "linear-gradient(135deg, #800000, #4A0000)", minWidth: "150px" }}
                disabled={uploading}
            >
              {uploading ? (
                  <><span className="spinner-border spinner-border-sm me-2"></span> Uploading...</>
              ) : (
                  <><i className="bi bi-cloud-arrow-up-fill me-2"></i> Upload Banner</>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* === BANNERS GRID (SCROLLABLE AREA) === */}
      <h6 className="fw-bold text-muted mb-3 text-uppercase" style={{ letterSpacing: "1px" }}>Active Banners ({banners.length})</h6>
      
      {/* 🔥 FIX: Scrollable Container added to prevent page stretch */}
      <div className="custom-scrollbar pe-2" style={{ maxHeight: "calc(100vh - 380px)", overflowY: "auto", overflowX: "hidden" }}>
          {loading ? (
            // 🔥 SHIMMER LOADER FOR GRID
            <div className="row g-4 placeholder-glow">
              {[1, 2, 3].map((i) => (
                <div key={i} className="col-12 col-md-6 col-lg-4">
                  <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="placeholder bg-secondary opacity-25 w-100" style={{ height: "160px" }}></div>
                    <div className="card-body d-flex justify-content-between align-items-center">
                        <span className="placeholder col-4 bg-secondary opacity-10 rounded"></span>
                        <span className="placeholder col-3 bg-danger opacity-25 rounded-pill" style={{ height: "35px" }}></span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : banners.length === 0 ? (
            // NO BANNERS STATE
            <div className="text-center py-5">
                <div className="d-inline-flex align-items-center justify-content-center bg-white rounded-circle shadow-sm mb-3" style={{ width: "80px", height: "80px" }}>
                    <i className="bi bi-image text-muted opacity-50 display-4"></i>
                </div>
                <h5 className="fw-bold text-dark">No Banners Found</h5>
                <p className="text-muted small">Your home page slider is currently hidden. Add a banner above to make it live.</p>
            </div>
          ) : (
            // ACTUAL BANNERS GRID
            <div className="row g-4 pb-3">
              {banners.map((banner, index) => (
                <div key={banner._id} className="col-12 col-md-6 col-lg-4">
                  <div className="card border-0 shadow-sm rounded-4 overflow-hidden hover-card h-100 d-flex flex-column">
                    
                    {/* Image Preview */}
                    <div className="position-relative bg-light" style={{ height: "160px" }}>
                        <img 
                            src={banner.imageUrl} 
                            alt={`Banner ${index}`} 
                            className="w-100 h-100 object-fit-cover"
                            onError={(e) => { e.target.src = "https://via.placeholder.com/600x200?text=Invalid+Image+URL" }}
                        />
                        <span className="badge bg-success position-absolute top-0 start-0 m-3 shadow-sm">Active</span>
                    </div>

                    {/* Card Footer Actions */}
                    <div className="card-body mt-auto d-flex justify-content-between align-items-center bg-white">
                        <small className="text-muted text-truncate w-50" title={banner.imageUrl}>
                            <i className="bi bi-link me-1"></i> Image Link
                        </small>
                        <button 
                            className="btn btn-outline-danger rounded-pill btn-sm px-3 fw-bold d-flex align-items-center"
                            onClick={() => setConfirmModal(banner._id)} // 🔥 Shows Custom Confirm Modal
                            disabled={deletingId === banner._id}
                        >
                            {deletingId === banner._id ? (
                                <><span className="spinner-border spinner-border-sm me-2"></span> Deleting...</>
                            ) : (
                                <><i className="bi bi-trash3-fill me-1"></i> Delete</>
                            )}
                        </button>
                    </div>

                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      {/* 🔥 PREMIUM CONFIRM DELETE MODAL 🔥 */}
      {confirmModal && (
          <>
              <div className="modal-backdrop fade show" style={{ backdropFilter: "blur(5px)", backgroundColor: "rgba(0,0,0,0.7)", zIndex: 1080 }}></div>
              <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1090 }}>
                  <div className="modal-dialog modal-dialog-centered px-4" style={{ maxWidth: "380px", margin: "auto" }}>
                      <div className="modal-content border-0 shadow-lg rounded-4 p-4 text-center animate-bounce">
                          <div className="mb-3 text-danger">
                              <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" style={{width: '70px', height: '70px'}}>
                                  <i className="bi bi-trash3-fill display-4"></i>
                              </div>
                          </div>
                          <h4 className="fw-bold mb-2 text-dark">Delete Banner?</h4>
                          <p className="text-muted mb-4 px-2" style={{ fontSize: "14px" }}>
                              Are you sure you want to delete this banner? It will be removed from the user app immediately.
                          </p>
                          <div className="d-flex gap-2">
                              <button className="btn btn-light w-50 rounded-pill fw-bold" onClick={() => setConfirmModal(null)}>
                                  Cancel
                              </button>
                              <button className="btn btn-danger w-50 rounded-pill fw-bold shadow-sm" onClick={executeDelete}>
                                  Yes, Delete
                              </button>
                          </div>
                      </div>
                  </div>
              </div>
          </>
      )}

      {/* 🔥 PREMIUM ALERT MODAL (Success/Error) 🔥 */}
      {alertModal.show && (
          <>
              <div className="modal-backdrop fade show" style={{ backdropFilter: "blur(5px)", backgroundColor: "rgba(0,0,0,0.7)", zIndex: 1080 }}></div>
              <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1090 }}>
                  <div className="modal-dialog modal-dialog-centered px-4" style={{ maxWidth: "380px", margin: "auto" }}>
                      <div className="modal-content border-0 shadow-lg rounded-4 p-4 text-center animate-bounce">
                          <div className={`mb-3 text-${alertModal.type === 'error' ? 'danger' : 'success'}`}>
                              <i className={`bi bi-${alertModal.type === 'error' ? 'x-circle-fill' : 'check-circle-fill'} display-1`}></i>
                          </div>
                          <h4 className="fw-bold mb-2 text-dark">
                              {alertModal.type === 'error' ? 'Oops!' : 'Success!'}
                          </h4>
                          <p className="text-muted mb-4 px-2" style={{ fontSize: "15px" }}>
                              {alertModal.message}
                          </p>
                          <button
                              className={`btn w-100 rounded-pill py-3 fw-bold shadow-sm text-white`}
                              style={{ background: alertModal.type === 'error' ? '#dc3545' : '#198754', border: "none" }}
                              onClick={() => setAlertModal({ show: false, message: "", type: "success" })}
                          >
                              Okay
                          </button>
                      </div>
                  </div>
              </div>
          </>
      )}

      {/* CSS Animations */}
      <style>{`
        .animate-fade-in { animation: fadeIn 0.4s ease-out; }
        .spin-animation { animation: spin 1s linear infinite; }
        .hover-card { transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out; }
        .hover-card:hover { transform: translateY(-5px); box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important; }
        .animate-bounce { animation: bounceIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) both; }
        
        /* Custom Scrollbar for Grid */
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #9ca3af; }

        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { 100% { transform: rotate(360deg); } }
        @keyframes bounceIn {
            0% { transform: scale(0.5); opacity: 0; }
            80% { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
        }
      `}</style>

    </div>
  );
}

export default ManageBanners;